import type { User, Task, PhotoWithUser, LikeResponse, ReactionResponse, SiteSettings, Analytics, TaskWithCreator, OnlineUser, AdminPhoto } from '@shared/types';

const API_BASE = '/api';
const SITE_DISABLED_EVENT = 'photobooth:site-disabled';

export class SiteDisabledError extends Error {
  constructor(public readonly disabledMessage: string) {
    super(disabledMessage);
    this.name = 'SiteDisabledError';
  }
}

function notifySiteDisabled(disabledMessage: string): void {
  window.dispatchEvent(
    new CustomEvent(SITE_DISABLED_EVENT, {
      detail: { disabledMessage },
    })
  );
}

export function subscribeToSiteDisabled(
  callback: (disabledMessage: string) => void
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<{ disabledMessage: string }>).detail.disabledMessage);
  };

  window.addEventListener(SITE_DISABLED_EVENT, handler);
  return () => window.removeEventListener(SITE_DISABLED_EVENT, handler);
}

async function readErrorResponse(responseText: string): Promise<Error> {
  const fallback = new Error('Request failed');

  if (!responseText) {
    return fallback;
  }

  try {
    const errorData = JSON.parse(responseText) as { error?: string; message?: string };
    if (errorData.error === 'SITE_DISABLED') {
      const disabledMessage = errorData.message || 'The photo booth will be available soon!';
      notifySiteDisabled(disabledMessage);
      return new SiteDisabledError(disabledMessage);
    }

    return new Error(errorData.error || errorData.message || 'Request failed');
  } catch {
    return fallback;
  }
}

// Get stored user ID
export function getUserId(): string | null {
  return localStorage.getItem('userId');
}

// Set stored user ID
export function setUserId(id: string): void {
  localStorage.setItem('userId', id);
}

// Clear stored user ID
export function clearUserId(): void {
  localStorage.removeItem('userId');
}

// Request headers with user ID
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const userId = getUserId();
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  return headers;
}

// Generic fetch wrapper
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw await readErrorResponse(await response.text());
  }

  return response.json();
}

// User API
export async function createUser(username: string): Promise<{ id: string; username: string; currentTask: Task | null }> {
  const result = await fetchApi<{ id: string; username: string; currentTask: Task | null }>('/users', {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
  setUserId(result.id);
  return result;
}

export async function getUser(userId: string): Promise<User & { currentTask: Task | null }> {
  return fetchApi(`/users/${userId}`);
}

// Task API
export async function getCurrentTask(): Promise<{ task: Task | null; message?: string }> {
  return fetchApi('/tasks/current');
}

export async function skipTask(): Promise<{ newTask: Task | null; message?: string }> {
  return fetchApi('/tasks/skip', { method: 'POST' });
}

export async function createTask(description: string): Promise<{ task: Task }> {
  return fetchApi('/tasks', {
    method: 'POST',
    body: JSON.stringify({ description }),
  });
}

export async function listTasks(): Promise<{ tasks: Task[] }> {
  return fetchApi('/tasks');
}

// Photo API
interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export async function uploadPhoto(
  image: Blob, 
  taskId: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<{ photo: PhotoWithUser; newTask: Task | null }> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', image, 'photo.jpg');
    formData.append('taskId', taskId);

    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          reject(new Error('Invalid response'));
        }
      } else {
        readErrorResponse(xhr.responseText).then(reject);
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('POST', `${API_BASE}/photos`);
    
    const userId = getUserId();
    if (userId) {
      xhr.setRequestHeader('X-User-Id', userId);
    }

    xhr.send(formData);
  });
}

export async function getPhotos(
  cursor?: string, 
  limit: number = 20,
  sort: 'newest' | 'likes' = 'newest'
): Promise<{ photos: PhotoWithUser[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  params.set('sort', sort);
  return fetchApi(`/photos?${params}`);
}

export async function getPhoto(photoId: string): Promise<{ photo: PhotoWithUser }> {
  return fetchApi(`/photos/${photoId}`);
}

export async function toggleLike(photoId: string): Promise<LikeResponse> {
  return fetchApi(`/photos/${photoId}/like`, { method: 'POST' });
}

export async function addReaction(photoId: string, emoji: string): Promise<ReactionResponse> {
  return fetchApi(`/photos/${photoId}/react`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

export async function removeReaction(photoId: string): Promise<ReactionResponse> {
  return fetchApi(`/photos/${photoId}/react`, { method: 'DELETE' });
}

// Admin API
export async function adminLogin(password: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error((errorData as { error?: string }).error || 'Login failed');
  }

  return response.json();
}

export async function adminLogout(): Promise<void> {
  await fetch(`${API_BASE}/admin/logout`, { method: 'POST' });
}

export async function verifyAdmin(): Promise<{ authenticated: boolean }> {
  return fetchApi('/admin/verify');
}

export async function getAnalytics(): Promise<Analytics> {
  return fetchApi('/admin/analytics');
}

export async function getSettings(): Promise<SiteSettings> {
  return fetchApi('/admin/settings');
}

export async function updateSettings(settings: Partial<SiteSettings>): Promise<{ success: boolean }> {
  return fetchApi('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

export async function getAdminPhotos(
  sortBy: 'newest' | 'oldest' | 'mostLiked' = 'newest',
  limit: number = 50
): Promise<{ photos: AdminPhoto[]; total: number }> {
  const params = new URLSearchParams();
  params.set('sortBy', sortBy);
  params.set('limit', String(limit));
  return fetchApi(`/admin/photos?${params}`);
}

export async function deleteAdminPhoto(photoId: string): Promise<{ success: boolean }> {
  return fetchApi(`/admin/photos/${photoId}`, { method: 'DELETE' });
}

export async function getAdminTasks(): Promise<{ tasks: TaskWithCreator[] }> {
  return fetchApi('/admin/tasks');
}

export async function createAdminTask(description: string): Promise<{ task: Task }> {
  return fetchApi('/admin/tasks', {
    method: 'POST',
    body: JSON.stringify({ description }),
  });
}

export async function updateAdminTask(taskId: string, updates: { description?: string; isActive?: boolean }): Promise<{ task: Task }> {
  return fetchApi(`/admin/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAdminTask(taskId: string): Promise<{ success: boolean }> {
  return fetchApi(`/admin/tasks/${taskId}`, { method: 'DELETE' });
}

export async function getAdminUsers(): Promise<{
  users: (User & { photoCount: number })[];
  onlineUsers: OnlineUser[];
  total: number;
}> {
  return fetchApi('/admin/users');
}

export async function deleteAdminUser(userId: string): Promise<{ success: boolean }> {
  return fetchApi(`/admin/users/${userId}`, { method: 'DELETE' });
}
